import React, { useEffect, useState } from 'react';
import cn from 'clsx';
import { useSearch } from '@okapi/router-react';
import { ChevronRightIcon } from '@fronton/icons-react';
import { createElementKey, removeSpecialCharacters } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { Modal } from '../modal';
import { Section } from '../section';
import { Typography } from '../typography';
import {
  benefitsCardGrid,
  benefitsCardGridMap,
  benefitsCardItem,
  benefitsCardItemContent,
  benefitsCardItemIcon,
  benefitsCardItemIconWrapper,
  benefitsCardItemImage,
  benefitsCardItemText,
  benefitsCardModal,
} from './benefits-card-styles';
import { BenefitsCardStructure } from './benefits-card-types';

type BenefitsCardProps<T> = {
  elementBlock: string;
  elementName: string;
  cardList: BenefitsCardStructure<T>[];
  renderModalContent: (card: BenefitsCardStructure<T>, onCloseModal: () => void) => React.ReactNode;
}

export const BenefitsCard = <T,>({
  cardList,
  elementBlock,
  elementName,
  renderModalContent,
}: BenefitsCardProps<T>) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [searchParams] = useSearch();
  const [activeCard, setActiveCard] = useState<BenefitsCardStructure<T> | null>(null);

  useEffect(() => {
    cardList?.forEach(item => {
      if (item.name && searchParams.benefits === item.name) {
        setActiveCard(item);
        setIsModalOpen(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemClick = (item: BenefitsCardStructure<T>) => () => {
    setActiveCard(item);
    setIsModalOpen(true);

    analyticsUtils.clickButtonEvent(
      elementBlock,
      removeSpecialCharacters(item.text),
      elementName
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveCard(null);
    if (activeCard) {
      analyticsUtils.clickButtonEvent(elementBlock, 'Закрыть', activeCard.elementName);
    }
  };

  return (
    <Section>
      <ul className={cn(benefitsCardGrid, benefitsCardGridMap[cardList.length])}>
        {cardList.map((item, index) => (
          <li key={createElementKey('header-item', index)}>
            <button className={benefitsCardItem} onClick={handleItemClick(item)}>
              <img
                className={
                  cn(benefitsCardItemImage)
                }
                src={item.icon.src}
                alt={item.icon.alt}
                width="36"
                height="36"
              />
              <div className={benefitsCardItemContent}>
                <Typography
                  className={benefitsCardItemText}
                  variant="m"
                  size="body_long"
                >
                  {item.text}
                </Typography>
                <div className={benefitsCardItemIconWrapper}>
                  <ChevronRightIcon
                    className={benefitsCardItemIcon}
                    type="outline"
                    size="xl"
                    color="text-minor"
                  />
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>

      {
        activeCard && (
          <Modal
            className={benefitsCardModal}
            onClose={handleCloseModal}
            show={isModalOpen}
            size="m"
            variant="curtain"
          >
            {renderModalContent(activeCard, handleCloseModal)}
          </Modal>
        )
      }
    </Section>
  )
}
